import { Component, ChangeDetectorRef, OnInit, OnDestroy, Input, ElementRef, HostListener, ViewChild, QueryList, ViewChildren, signal } from '@angular/core';
import { LoadingComponent } from '@/components/loading/loading.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../apiConnection/ApiService';
import { Urls } from '../../../apiConnection/urls';
import { CommonModule } from '@angular/common';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Utils } from '@/utils/utils';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { ProviderFormComponent } from '@/pages/providers/provider-form/provider-form.component';
import { DropdownCoordinatorService } from '@/components/dropdown-coordinator.service';
import { OfflineQueueService } from '@/offline/offline-queue.service';
import { SyncService } from '@/offline/sync.service';
import { WriteQueueEntry } from '@/offline/db';
import { DocumentDeviceMonitorService } from '@/offline/document-device-monitor.service';
import { DocumentIdCache } from '@/utils/document-id-cache';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';

@Component({
  imports: [
    LoadingComponent,
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    CustomInputComponent,
    CustomSelectComponent,
    RouterModule,
  ],
  templateUrl: './purchases-form.page.html'
})
export class PurchaseFormPage implements OnInit, OnDestroy {
  public loading = signal(false);
  public saving = signal(false);
  public saveForm!: FormGroup;
  public showResults = false;
  public filteredProducts: any[] = [];
  public dropdowns: any = {
    providers: [],
    users: [],
    payMethods: [],
    products: [],
    offices: [],
    tax_config: []
  };
  type: string | null = null;

  public providerFormComponent = ProviderFormComponent

  @Input() id: number | string = 0;

  // hidden items dropdown when click outside
  @ViewChild('mainSearch') searchContainer!: ElementRef;
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
      this.showResults = false;
    }
  }

  public pendingSyncEntry: WriteQueueEntry | null = null;

  private openedSubscription?: Subscription;
  private resolvedSubscription?: Subscription;

  openProductSearch() {
    // stopPropagation() en otros dropdowns (custom-select) impide que este
    // onClickOutside se dispare, así que avisamos explícitamente al abrirnos
    this.coordinator.notifyOpened(this);
    this.showResults = true;
  }

  @ViewChildren('productListQuantity') quantityInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private coordinator: DropdownCoordinatorService,
    private offlineQueue: OfflineQueueService,
    private syncService: SyncService,
    private documentDeviceMonitor: DocumentDeviceMonitorService,
  ) {
    // TODO: deprecated, change in the future 😅
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  get isProviderSelected() {
    return this.saveForm.get('provider_id')?.value > 0
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {

      this.type = params['type'] || 'invoice';
      this.id = params['id'] || 0;

      this.createForm();
      this.getDropdowns();

      if (this.id != 0) {
        this.getById(this.id);
      } else {
        this.getIdByCompany();
      }

      this.checkPendingSync();

    })

    // Si un custom-select se abre, cerramos el dropdown de búsqueda de productos
    this.openedSubscription = this.coordinator.opened$.subscribe(source => {
      if (source !== this && this.showResults) {
        this.showResults = false;
      }
    });

    // Si esta compra (temp id o pendiente de actualización) termina de sincronizarse
    // mientras el formulario sigue abierto, recargamos contra el id real ya asignado.
    this.resolvedSubscription = this.syncService.resolved$.subscribe(({ tempId, realId, entityType }) => {
      if (entityType === 'purchases' && tempId === Number(this.id)) {
        this.refresh(realId);
      }
    });
  }

  ngOnDestroy(): void {
    this.openedSubscription?.unsubscribe();
    this.resolvedSubscription?.unsubscribe();
  }

  private async checkPendingSync(): Promise<void> {
    this.pendingSyncEntry = await this.offlineQueue.findPendingForEntity('purchases', Number(this.id)) ?? null;
    this.cdr.detectChanges();
  }

  focusOnLastProduct() {
    setTimeout(() => {
      const lastInputRef = this.quantityInputs.last;

      if (lastInputRef && lastInputRef.nativeElement) {
        lastInputRef.nativeElement.focus();
        lastInputRef.nativeElement.select();
      }
    }, 100);
  }

  changeType() {
    const typeControl = this.saveForm.get('type');

    const currentType = typeControl?.value;

    const options: Record<string, string> = {
      'factura': 'invoice',
      'albaran': 'delivery-note',
      'devolucion': 'refund'
    }

    this.router.navigate(['/purchase/new/' + (options[currentType] || options['factura'])]);
  }

  createForm() {

    const options: Record<string, string> = {
      'invoice': 'factura',
      'delivery-note': 'albaran',
      'refund': 'devolucion'
    }

    const type = this.type ? options[this.type] || options['invoice'] : options['invoice'];

    this.saveForm = new FormGroup({
      id_by_company: new FormControl(null),
      year: new FormControl(new Date().getFullYear(), [Validators.required]),
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      pay_id: new FormControl(null),
      user_id: new FormControl(null),
      discount_absolute_bool: new FormControl(0),
      discount: new FormControl(0),
      notes: new FormControl(null),
      cod: new FormControl(null),
      provider_id: new FormControl(null, [Validators.required]),
      type: new FormControl(type, [Validators.required]),
      document_ref: new FormControl(null),
      invoice_split: new FormControl(null),
      invoice_credit_bool: new FormControl(0),
      subtotal: new FormControl('0.00'),
      total: new FormControl('0.00'),
      items: new FormArray([]),
      office_id: new FormControl(null),
      receipt_date: new FormControl(null),
    });

    this.saveForm.valueChanges.subscribe(() => this.calculateTotals());

    // En una compra nueva, el número depende de la oficina y el año elegidos
    this.saveForm.get('office_id')?.valueChanges.subscribe(() => {
      if (this.id == 0) this.getIdByCompany();
    });
    this.saveForm.get('year')?.valueChanges.subscribe(() => {
      if (this.id == 0) this.getIdByCompany();
    });
  }

  get items() { return this.saveForm.get('items') as FormArray; }
  get itemControls() { return this.items.controls; }

  changeProvider() {
    const id = this.saveForm.get('provider_id')?.value;
    const provider = this.dropdowns.providers.find((p: any) => p.id == id);

    if (provider && provider.payment_method_id) {
      this.saveForm.get('pay_id')?.setValue(provider.payment_method_id)
    }
  }

  getDropdowns() {
    const config = { providers: {}, products: {}, pay_methods: {}, users: {}, offices: {}, companies: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];

        this.dropdowns.tax_config = company.tax_config ? JSON.parse(company.tax_config) : [];

        this.dropdowns.providers = data.providers || [];
        this.dropdowns.products = data.products || [];
        this.dropdowns.payMethods = data.pay_methods || [];
        this.dropdowns.users = data.users || [];
        this.dropdowns.offices = data.offices || [];
        this.filteredProducts = [];

        if (this.id == 0) {
          const currentUserId = this.authenticationService.currentUserValue.id
          const userId = this.dropdowns.users.find((user: { id: number }) => user.id == currentUserId)
            ? currentUserId
            : this.dropdowns.users[0]?.id;

          this.saveForm.get('user_id')?.setValue(userId);
          // Con oficinas, la oficina es obligatoria (el backend numera por oficina y lo valida)
          if (this.dropdowns.offices.length) {
            this.saveForm.get('office_id')?.addValidators(Validators.required);
          }
          this.saveForm.get('office_id')?.setValue(this.dropdowns.offices[0]?.id ?? null);
        }

        this.cdr.detectChanges();
      }
    });
  }

  filterProducts(query: string) {
    if (!query) {
      this.filteredProducts = [];
      return;
    }

    this.openProductSearch();

    const q = query.toLowerCase();
    this.filteredProducts = this.dropdowns.products.filter((p: any) =>
      p.name.toLowerCase().includes(q) ||
      p.id.toString().includes(q) ||
      (p.code && p.code.toLowerCase().includes(q))
    );
  }

  addFirstProduct(inputElement: HTMLInputElement) {
    if (this.filteredProducts.length > 0) {
      const firstProduct = this.filteredProducts[0];
      this.selectProductFromSearch(firstProduct, inputElement);
    }
  }

  selectProductFromSearch(product: any, inputElement: HTMLInputElement) {
    console.log('Selected product:', product);
    this.addItem({
      id: null,
      product_id: product.id,
      name: product.name,
      price: (product.cost || 0) / 100,
      quantity: 1,
      discount: 0,
      special_measures: null,
      tax: this.getProductTaxQuotient(product),
    });

    inputElement.value = '';
    inputElement.focus()
    this.showResults = false;
    this.filteredProducts = [];
    this.focusOnLastProduct()
  }

  addItem(itemData: any = null) {
    const itemGroup = new FormGroup({
      id: new FormControl(itemData?.id || null),
      product_id: new FormControl(itemData?.product_id || null, [Validators.required]),
      name: new FormControl(itemData?.name || 'Nuevo artículo'),
      quantity: new FormControl(itemData?.quantity || 1, [Validators.required, Validators.min(1)]),
      discount: new FormControl(itemData?.discount || 0),
      price: new FormControl(itemData?.price || 0),
      special_measures: new FormControl(itemData?.special_measures || null),
      tax: new FormControl(itemData?.tax ?? null),
      apply_tax: new FormControl(itemData?.apply_tax ?? 0),
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number) {
    this.items.removeAt(index);
    this.calculateTotals();
  }

  calculateTotals() {
    let sub = 0;
    let tot = 0;

    this.items.controls.forEach(c => {
      sub += Number(this.getAndCalculateWithOutTax(c));
      tot += Number(this.getAndCalculateWithTax(c));
    });

    const disc = this.saveForm.get('discount')?.value || 0;
    const isAbsolute = Number(this.saveForm.get('discount_absolute_bool')?.value) === 1;

    const lastSub = sub;

    if (isAbsolute) {
      sub -= disc;
    } else {
      sub *= (1 - (disc / 100));
    }

    tot = lastSub > 0 ? sub * tot / lastSub : 0;

    if (isNaN(tot)) {
      tot = 0;
    }

    this.saveForm.get('subtotal')?.setValue(sub.toFixed(2), { emitEvent: false });
    this.saveForm.get('total')?.setValue(tot.toFixed(2), { emitEvent: false });
  }

  showOfficeName() {
    return this.dropdowns.offices.find((o: any) => o.id == this.saveForm.get('office_id')?.value)?.name;
  }

  showBuyer() {
    return this.dropdowns.users.find((u: any) => u.id === this.saveForm.get('user_id')?.value)?.name
  }

  // El backend numera por oficina + año + tipo: hay que pedir el número con la misma
  // oficina y año con que se va a guardar, o el formulario muestra otro distinto.
  private idByCompanyRequest = 0;

  getIdByCompany() {
    const params = new URLSearchParams();
    const officeId = this.saveForm.get('office_id')?.value;
    const year = this.saveForm.get('year')?.value;
    if (officeId) params.set('office_id', officeId);
    if (year) params.set('year', year);

    // Si cambian oficina/año con una petición en curso, solo vale la última respuesta
    const request = ++this.idByCompanyRequest;

    this.apiService.getPetition(Urls.urls.purchases + '/last-id-by-company/' + this.type, '?' + params.toString()).subscribe((data: any) => {
      if (request !== this.idByCompanyRequest) return;
      this.saveForm.patchValue({ id_by_company: data.last_id_by_company + 1 }, { emitEvent: false });
      this.cdr.detectChanges();
    });
  }

  getById(id: number | string) {
    this.loading.set(true);
    this.apiService.getById(Urls.urls.purchases, id).subscribe({
      next: (data: any) => {
        const purchase = data.purchase || {};

        let invoiceSplit = purchase.invoice_split;
        if (typeof invoiceSplit === 'string') {
          try {
            invoiceSplit = JSON.parse(invoiceSplit);
          } catch (e) {
            invoiceSplit = null;
          }
        }

        this.saveForm.patchValue({
          id_by_company: purchase.id_by_company ?? null,
          year: purchase.year ?? new Date().getFullYear(),
          date: purchase.date ? purchase.date.split(' ')[0] : new Date().toISOString().split('T')[0],
          pay_id: purchase.pay_id ?? null,
          user_id: purchase.user_id ?? this.authenticationService.currentUserValue.id,
          discount_absolute_bool: purchase.discount_absolute_bool ?? 0,
          discount: purchase.discount ?? 0,
          notes: purchase.notes ?? null,
          cod: purchase.cod ?? null,
          provider_id: purchase.provider_id ?? null,
          type: purchase.type ?? this.saveForm.get('type')?.value,
          document_ref: purchase.document_ref ?? null,
          invoice_split: invoiceSplit ?? null,
          invoice_credit_bool: purchase.invoice_credit_bool ?? 0,
          subtotal: (purchase.subtotal !== undefined && purchase.subtotal !== null) ? (Number(purchase.subtotal) / 100).toFixed(2) : '0.00',
          total: (purchase.total !== undefined && purchase.total !== null) ? (Number(purchase.total) / 100).toFixed(2) : '0.00',
          office_id: purchase.office_id ?? null,
          status: null
        });

        const details = data.purchase_details || [];
        while (this.items.length) {
          this.items.removeAt(0);
        }

        details.forEach((d: any) => {
          const pivot = d.pivot || {};
          this.items.push(new FormGroup({
            id: new FormControl(pivot.id || null),
            product_id: new FormControl(d.id ?? null, [Validators.required]),
            name: new FormControl(pivot.personalized_desc || d.name || 'Nuevo artículo'),
            quantity: new FormControl(pivot.quantity ?? 1, [Validators.required, Validators.min(1)]),
            discount: new FormControl(pivot.discount ?? 0),
            price: new FormControl((pivot.price !== undefined && pivot.price !== null) ? (Number(pivot.price) / 100) : 0),
            special_measures: new FormControl(pivot.special_measures ?? null),
            tax: new FormControl(pivot.tax ?? this.getProductTaxQuotient(d)),
            apply_tax: new FormControl(pivot.apply_tax ? 1 : 0),
          }));
        });

        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('ERROR al obtener la compra. ' + error);
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  saveEntity() {
    this.submit(null, false);
  }

  // Draft: stored without touching stock (the backend skips stock/medium-price updates for status 'draft').
  saveDraft() {
    this.submit('draft', false);
  }

  saveAndPrint() {
    this.submit(null, true);
  }

  private submit(status: 'draft' | null, print: boolean) {
    if (this.saveForm.valid) {
      if (!this.documentDeviceMonitor.canGenerateOffline()) {
        Utils.notification.alert('Sin conexión: este ordenador no está autorizado para generar compras sin internet.');
        return;
      }
      // Solo un borrador puede guardarse sin líneas (el backend rechaza el resto).
      if (this.items.length === 0) {
        Utils.notification.alert('Añade al menos un producto antes de guardar.');
        return;
      }

      this.saving.set(true);
      const payload = {
        ...this.saveForm.value,
        status
      };

      payload.subtotal = Math.abs(payload.subtotal * 100)
      payload.total = Math.abs(payload.total * 100)

      payload.items = payload.items.map((item: any) => ({
        ...item,
        price: Math.abs(item.price * 100),
        personalized_desc: this.personalizedDesc(item),
      }));

      const invoiceSplit = Array.isArray(payload.invoice_split) ? payload.invoice_split : [];
      payload.invoice_split = invoiceSplit.length ? invoiceSplit : null;

      this.apiService.save(Urls.urls.purchases, payload, this.id).subscribe({
        next: (result: any) => {
          const id = result.purchase.id;
          DocumentIdCache.record('purchases', payload.type, id, result.purchase.id_by_company);
          if (print) {
            window.open(`/purchase-invoice/print/${id}`, '_blank');
          } else {
            Utils.notification.success(status === 'draft'
              ? 'Borrador guardado. El stock no se ha modificado.'
              : 'Compra guardada correctamente. Stock actualizado.')
          }
          this.refresh(id)
        },
        error: (error: any) => {
          this.saving.set(false)
          Utils.notification.alert(Utils.extractErrorMessage(error) || "Error al guardar la compra")
          this.cdr.detectChanges()
        },
        complete: () => {
          this.saving.set(false)
        }
      });
    }
  }

  refresh(id: number) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/purchase']);
    });
  }

  // Persist a manually-edited line name as personalized_desc; if it still
  // matches the underlying product's real name, don't store an override.
  private personalizedDesc(item: any): string | null {
    const originalName = (this.getProductInfo(item.product_id)?.name || '').trim();
    const currentName = (item.name || '').trim();
    return (currentName && currentName !== originalName) ? currentName : null;
  }

  getProductInfo(productId: any) {
    if (!productId || !this.dropdowns.products) return null;
    return this.dropdowns.products.find((p: any) => p.id == productId);
  }

  getProductTaxQuotient(product: any): number | null {
    if (!product || !this.dropdowns.tax_config) return null;
    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product.tax_type);
    return taxConfig?.quotient ? Number(taxConfig.quotient) : null;
  }

  getAndCalculateWithOutTax(item: any) {
    const quantity = item.get('quantity')?.value || 0
    const price = item.get('price')?.value || 0
    const discount = (100 - (item.get('discount')?.value || 0)) / 100

    return ((quantity * price) * discount).toFixed(2)
  }

  getAndCalculateWithTax(item: any) {
    const product_id = item.get('product_id')?.value;
    const product = this.dropdowns.products.find((p: any) => p.id == product_id);

    const taxConfig = this.dropdowns.tax_config.find((t: any) => t.id == product?.tax_type);

    const quotient = taxConfig && taxConfig.quotient ? Number(taxConfig.quotient) : 21;

    return (Number(this.getAndCalculateWithOutTax(item)) * ((quotient + 100) / 100)).toFixed(2)
  }

  onDiscountTab(event: KeyboardEvent, isLast: boolean, searchInput: HTMLInputElement): void {
    if (isLast) {
      event.preventDefault();
      searchInput.focus();
    }
  }

  onSearchProductTab(event: Event, isLast: boolean): void {
    if (isLast) {
      this.showResults = false;
    }
  }
}
